// =========================================================================
// React-only entry point — keeps react out of the vanilla bundle. Host
// apps using the hook MUST already have react + react-router-dom on
// their dep graph (both are peerDeps, optional).
// =========================================================================

import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { trackPageview, type TrackPageviewOpts } from './vanilla';

export interface UseRouteTrackingOpts extends TrackPageviewOpts {
  /**
   * When true, suppress the hook entirely (e.g. while the auth provider
   * is loading and routing decisions are still in flight). Defaults to
   * false.
   */
  paused?: boolean;
}

/**
 * Fire `trackPageview()` on every react-router location change. Must be
 * rendered INSIDE the router (so `useLocation()` works) and is safe to
 * call before `initAnalytics()` resolves — until the client is wired
 * the trackPageview() implementation no-ops.
 *
 * Usage (inside `<BrowserRouter>` + auth provider):
 *
 *     useRouteTracking({ vendor_slug: vendor?.slug });
 */
export function useRouteTracking(opts: UseRouteTrackingOpts = {}): void {
  const { paused, ...trackOpts } = opts;
  const location = useLocation();
  useEffect(() => {
    if (paused) return;
    trackPageview(trackOpts);
    // We intentionally key on pathname + search so a query-string-only
    // change (e.g. ?utm_source=...) still records the new attribution
    // bucket, but a state-only navigation (location.key change without
    // url change) does NOT double-fire.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search, paused]);
}
